import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil, catchError, finalize } from 'rxjs/operators';
import { Announcement } from '../../../models/announcement.model';
import { AnnouncementService } from '../../../services/announcement.service';
import { FormatDateService } from 'src/app/services/formatdate.service';
import { ToastNotificationService } from '../../../services/toast-notification.service';

@Component({
  selector: 'app-announcements-dialog',
  templateUrl: './announcements-dialog.component.html',
  styleUrls: ['./announcements-dialog.component.scss']
})
export class AnnouncementsDialogComponent implements OnInit, OnDestroy, OnChanges {
  @Input() visible: boolean = false;
  @Input() announcements: Announcement[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() acknowledge = new EventEmitter<Announcement[]>();

  private destroyed$ = new Subject<void>();
  sanitizedAnnouncements: Array<{ announcement: Announcement; sanitizedContent: SafeHtml }> = [];
  acknowledging: boolean = false;

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly announcementService: AnnouncementService,
    private readonly toastNotificationService: ToastNotificationService,
    private readonly formatDateService: FormatDateService
  ) {}

  ngOnInit(): void {
    // Sanitize announcements when they change
    this.updateSanitizedAnnouncements();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['announcements'] && !changes['announcements'].firstChange) {
      this.updateSanitizedAnnouncements();
    }
  }

  private updateSanitizedAnnouncements(): void {
    if (!this.announcements || !Array.isArray(this.announcements)) {
      this.sanitizedAnnouncements = [];
      return;
    }
    this.sanitizedAnnouncements = this.announcements.map(announcement => ({
      announcement,
      sanitizedContent: this.sanitizer.sanitize(1, announcement.content) || ''
    }));
  }

  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onAcknowledgeAll(): void {
    if (this.acknowledging || this.announcements.length === 0) {
      return;
    }

    this.acknowledging = true;
    
    // Close dialog immediately
    this.acknowledge.emit(this.announcements);
    this.onClose();
    
    // Process acknowledgment in background
    this.announcementService.acknowledgeAnnouncements()
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => this.acknowledging = false),
        catchError(error => {
          this.toastNotificationService.showError('Failed to acknowledge announcements. Please try again later.');
          return [];
        })
      )
      .subscribe();
  }

  hasAnnouncements(): boolean {
    return this.announcements.length > 0;
  }
}

